import React, { forwardRef } from "react";
import ReactSwipeableBottomSheet from "./SwipeableBottomSheet";

import Typography from "../Typography";
import {
    BottomSheetContentWrapper,
    BottomSheetCrossIconWrapper,
    BottomSheetSectionWrapper,
    BottomSheetWrapper,
} from "./BottomSheet.styles";

import CrossIcon from "../../assets/icons/sign/cross.svg";

import COLORS from "../../constants/colors";
import { BottomSheetProps } from "./BottomSheet.types";
import zIndexList from "../../constants/zIndexList";
import { useViewportSheetDomSync } from "../../hooks/useViewportSheetDomSync";
import {
    getViewportSheetContainerStyle,
    usesViewportSheetLayout,
} from "../../methods/viewportSheet";

const BottomSheet = forwardRef<HTMLDivElement, BottomSheetProps>(
    (props: BottomSheetProps, ref) => {
        const {
            isOpen = false,
            id,
            overflowHeight,
            defaultOpen,
            marginTop,
            paddingBottom,
            onToggle,
            children,
            renderTitle,
            titleText,
            titleTextSize = "l",
            renderFooter,
            bodyHeight,
            bodyMinHeight,
            bodyMaxHeight = "70vh",
            fullScreen = false,
            overlay = true,
            showCloseIcon = true,
            showHeader = true,
            padded = true,
            disableOverlayClick = true,
            disableSwipe = false,
            backgroundColor = COLORS.WHITE,
            borderRadius = 8,
            showBottomSheetOverlay = true,
            hideBodyScrollbar = false,
            blurBackground = false,
            disableSheetZoom = false,
            bodyStyle,
        } = props;

        const useViewportLayout = usesViewportSheetLayout(
            disableSheetZoom,
            fullScreen,
        );
        const viewportContainerStyle = useViewportLayout
            ? getViewportSheetContainerStyle()
            : undefined;

        useViewportSheetDomSync(isOpen && useViewportLayout);

        const handleOnClickClose = () => {
            onToggle?.(false);
        };
        const handleOnChange = (isOpen: boolean) => {
            onToggle?.(isOpen);
        };

        const Header = (
            <>
                {renderTitle ? (
                    renderTitle
                ) : (
                    <BottomSheetSectionWrapper data-popup-header>
                        {titleText ? (
                            <Typography weight="bold" size={titleTextSize}>
                                {titleText}
                            </Typography>
                        ) : null}

                        {showCloseIcon ? (
                            <BottomSheetCrossIconWrapper
                                onClick={handleOnClickClose}
                                id={id ? `${id}--closeBtn` : ""}
                            >
                                <CrossIcon fill={COLORS.GREY_900} />
                            </BottomSheetCrossIconWrapper>
                        ) : null}
                    </BottomSheetSectionWrapper>
                )}
            </>
        );

        const Body = (
            <BottomSheetSectionWrapper
                data-popup-body
                style={{
                    height: fullScreen
                        ? "100%"
                        : useViewportLayout
                        ? "auto"
                        : bodyHeight,
                    minHeight: useViewportLayout ? 0 : bodyMinHeight,
                    maxHeight: fullScreen
                        ? "unset"
                        : useViewportLayout
                        ? "none"
                        : bodyMaxHeight,
                    flex: useViewportLayout ? "1 1 auto" : undefined,
                    ...bodyStyle,
                }}
            >
                {children}
            </BottomSheetSectionWrapper>
        );
        const Footer = (
            <BottomSheetSectionWrapper data-popup-footer>
                {renderFooter}
            </BottomSheetSectionWrapper>
        );

        return isOpen ? (
            <BottomSheetWrapper borderRadius={borderRadius}>
                <ReactSwipeableBottomSheet
                    overflowHeight={overflowHeight}
                    open={isOpen}
                    defaultOpen={defaultOpen}
                    marginTop={marginTop}
                    shadowTip={false}
                    topShadow={false}
                    onChange={handleOnChange}
                    fullScreen={fullScreen}
                    overlay={showBottomSheetOverlay}
                    style={{ zIndex: zIndexList.bottomSheet }}
                    bodyStyle={viewportContainerStyle}
                    swipeableViewsProps={{
                        disabled: fullScreen || disableSwipe,
                        animateHeight: false,
                        containerStyle: viewportContainerStyle,
                    }}
                    overlayStyle={{
                        pointerEvents: "all",
                        ...(blurBackground
                            ? {
                                  backgroundColor: "rgba(10, 15, 30, 0.35)",
                                  backdropFilter: "blur(10px)",
                                  WebkitBackdropFilter: "blur(10px)",
                              }
                            : {}),
                        ...(disableSheetZoom ? { touchAction: "none" } : {}),
                    }}
                >
                    <BottomSheetContentWrapper
                        style={{ paddingBottom: paddingBottom }}
                        padded={padded}
                        backgroundColor={backgroundColor}
                        hideBodyScrollbar={hideBodyScrollbar}
                        disableSheetZoom={disableSheetZoom}
                    >
                        {showHeader ? Header : null}
                        {Body}
                        {renderFooter ? Footer : null}
                    </BottomSheetContentWrapper>
                </ReactSwipeableBottomSheet>
            </BottomSheetWrapper>
        ) : (
            <></>
        );
    },
);

export default BottomSheet;
